import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const NEW_UNIT = "__new__";
const EMPTY = { name: "", description: "", measure: "", unit: "", level_id: "", placement: "", preference: "" };
const PLACEMENTS = [
  { value: "suelo", label: "Suelos" },
  { value: "pared", label: "Paredes" },
  { value: "techo", label: "Techos" },
  { value: "espacio", label: "Espacios" },
  { value: "organizacion", label: "Organización" },
];

export default function WorkAreaModal({ open, onOpenChange, levels, units, defaultLevelId, area, onSaved }) {
  const editing = Boolean(area);
  const [form, setForm] = useState(EMPTY);
  const [unitMode, setUnitMode] = useState("");
  const [customUnit, setCustomUnit] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (area) {
      setForm({
        name: area.name || "", description: area.description || "",
        measure: area.measure ?? "", unit: area.unit || "", level_id: area.level_id || "",
        placement: area.placement || "", preference: area.preference || "",
      });
      setUnitMode(area.unit ? (units.includes(area.unit) ? area.unit : NEW_UNIT) : "");
      setCustomUnit(area.unit && !units.includes(area.unit) ? area.unit : "");
    } else {
      setForm({ ...EMPTY, level_id: defaultLevelId || (levels[0]?.level_id ?? "") });
      setUnitMode("");
      setCustomUnit("");
    }
  }, [open, area, defaultLevelId, levels, units]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Indica el nombre del área.");
    if (!form.level_id) return toast.error("Selecciona un nivel.");
    if (!form.placement) return toast.error("Selecciona la ubicación (suelo, pared, techo o espacio).");
    const unit = unitMode === NEW_UNIT ? customUnit.trim() : unitMode;
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      measure: form.measure === "" ? null : Number(form.measure),
      unit: unit || null,
      level_id: form.level_id,
      placement: form.placement,
      preference: form.preference.trim(),
    };
    try {
      const r = editing
        ? await api.put(`/model/work-areas/${area.area_id}`, payload)
        : await api.post("/model/work-areas", payload);
      onSaved(r.data, editing, unit);
      onOpenChange(false);
      toast.success(editing ? "Área actualizada." : "Área de trabajo añadida.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar el área.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="work-area-modal">
        <DialogHeader>
          <DialogTitle>{editing ? "Editar área de trabajo" : "Nueva área de trabajo"}</DialogTitle>
          <DialogDescription>Define el área y el nivel de la vivienda modelo al que pertenece.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="wa-name">Nombre del área *</Label>
            <Input id="wa-name" data-testid="work-area-name-input" placeholder="Ej. Salón principal"
              value={form.name} onChange={(e) => set("name", e.target.value)} autoFocus />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="wa-pref">Preferencia (orden dentro del nivel)</Label>
            <Input id="wa-pref" data-testid="work-area-preference-input" placeholder="Ej. 1, A, 1.2…"
              value={form.preference} onChange={(e) => set("preference", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="wa-desc">Descripción</Label>
            <Textarea id="wa-desc" data-testid="work-area-description-input" rows={2}
              placeholder="Breve descripción (opcional)" value={form.description} onChange={(e) => set("description", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="wa-measure">Medida</Label>
              <Input id="wa-measure" data-testid="work-area-measure-input" type="number" min="0" step="0.01"
                placeholder="0,00" value={form.measure} onChange={(e) => set("measure", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Ud. de medida</Label>
              <Select value={unitMode} onValueChange={setUnitMode}>
                <SelectTrigger data-testid="work-area-unit-select"><SelectValue placeholder="Unidad" /></SelectTrigger>
                <SelectContent>
                  {units.map((u) => (
                    <SelectItem key={u} value={u} data-testid={`work-area-unit-option-${u}`}>{u}</SelectItem>
                  ))}
                  <SelectItem value={NEW_UNIT} data-testid="work-area-unit-option-new">+ Nueva unidad…</SelectItem>
                </SelectContent>
              </Select>
              {unitMode === NEW_UNIT && (
                <Input className="mt-2" data-testid="work-area-custom-unit-input" placeholder="Nueva unidad (ej. kg)"
                  value={customUnit} onChange={(e) => setCustomUnit(e.target.value)} />
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Nivel *</Label>
              <Select value={form.level_id} onValueChange={(v) => set("level_id", v)}>
                <SelectTrigger data-testid="work-area-level-select"><SelectValue placeholder="Selecciona un nivel" /></SelectTrigger>
                <SelectContent>
                  {levels.map((l) => (
                    <SelectItem key={l.level_id} value={l.level_id} data-testid={`work-area-level-option-${l.level_id}`}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Espacio *</Label>
              <Select value={form.placement} onValueChange={(v) => set("placement", v)}>
                <SelectTrigger data-testid="work-area-placement-select"><SelectValue placeholder="Suelos, paredes…" /></SelectTrigger>
                <SelectContent>
                  {PLACEMENTS.map((p) => (
                    <SelectItem key={p.value} value={p.value} data-testid={`work-area-placement-option-${p.value}`}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground -mt-2">Usa <strong>Espacios</strong> cuando combina varias ubicaciones físicas (p. ej. suelo + pared); usa <strong>Organización</strong> cuando no hay relación física, sino organizativa.</p>
        </div>
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button type="button" className="rounded-full" onClick={submit} disabled={saving} data-testid="work-area-save-button">
            {saving ? "Guardando…" : editing ? "Guardar cambios" : "Añadir área"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
